import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import useMediaQuery from '@mui/material/useMediaQuery'
import TuneIcon from '@mui/icons-material/Tune'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import React from 'react'
import { useTheme } from '@mui/material/styles'
import { PAYMENT_TYPE_OPTIONS, TRANSACTION_STATUS_OPTIONS } from '../constants'
import {
  AdminBillingType,
  AdminSubscriptionFilters,
  AdminTransactionFilter,
} from '../utils'

interface Props {
  filters: AdminSubscriptionFilters
  onChangeFilter: <T extends keyof AdminSubscriptionFilters>(
    key: T,
    value: AdminSubscriptionFilters[T]
  ) => void
  statusOptions: string[]
}

export const SubscriptionsFilters: React.FC<Props> = ({
  filters,
  onChangeFilter,
  statusOptions,
}) => {
  const theme = useTheme()
  const isPhoneScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [showPhoneFilters, setShowPhoneFilters] = React.useState(false)

  const filterControls = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.25}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      sx={{ flexShrink: 0 }}
    >
      <FormControl
        size="small"
        sx={{ minWidth: { sm: 120, md: 136 }, width: { xs: '100%', sm: 'auto' } }}
      >
        <InputLabel id="subscription-status-filter-label">Status</InputLabel>
        <Select
          labelId="subscription-status-filter-label"
          label="Status"
          value={filters.status}
          onChange={(event) =>
            onChangeFilter(
              'status',
              event.target.value as AdminSubscriptionFilters['status']
            )
          }
        >
          {statusOptions.map((status) => (
            <MenuItem key={status} value={status}>
              {status === 'all' ? 'All statuses' : status}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        size="small"
        sx={{ minWidth: { sm: 112, md: 124 }, width: { xs: '100%', sm: 'auto' } }}
      >
        <InputLabel id="subscription-payment-filter-label">Billing</InputLabel>
        <Select
          labelId="subscription-payment-filter-label"
          label="Billing"
          value={filters.paymentType}
          onChange={(event) =>
            onChangeFilter(
              'paymentType',
              event.target.value as 'all' | AdminBillingType
            )
          }
        >
          {PAYMENT_TYPE_OPTIONS.map((paymentType) => (
            <MenuItem key={paymentType} value={paymentType}>
              {paymentType === 'all' ? 'All billing' : paymentType}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl
        size="small"
        sx={{ minWidth: { sm: 132, md: 148 }, width: { xs: '100%', sm: 'auto' } }}
      >
        <InputLabel id="subscription-transaction-filter-label">
          Latest charge
        </InputLabel>
        <Select
          labelId="subscription-transaction-filter-label"
          label="Latest charge"
          value={filters.transactionStatus}
          onChange={(event) =>
            onChangeFilter(
              'transactionStatus',
              event.target.value as AdminTransactionFilter
            )
          }
        >
          {TRANSACTION_STATUS_OPTIONS.map((status) => (
            <MenuItem key={status} value={status}>
              {status === 'all'
                ? 'All charge states'
                : status === 'none'
                  ? 'No linked charges'
                  : `${status.charAt(0).toUpperCase()}${status.slice(1)}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  )

  return (
    <Stack spacing={1.1}>
      {isPhoneScreen ? (
        <>
          <TextField
            label="Search subscriptions"
            size="small"
            fullWidth
            value={filters.search}
            onChange={(event) => onChangeFilter('search', event.target.value)}
            placeholder="Customer, contact info, unit, transaction, invoice"
          />
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<TuneIcon fontSize="small" />}
            endIcon={
              showPhoneFilters ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )
            }
            onClick={() => setShowPhoneFilters((current) => !current)}
            sx={{
              alignSelf: 'flex-start',
              whiteSpace: 'nowrap',
            }}
          >
            {showPhoneFilters ? 'Hide filters' : 'Filters'}
          </Button>
          <Collapse in={showPhoneFilters} unmountOnExit>
            {filterControls}
          </Collapse>
        </>
      ) : (
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <TextField
            label="Search subscriptions"
            size="small"
            fullWidth
            value={filters.search}
            onChange={(event) => onChangeFilter('search', event.target.value)}
            placeholder="Customer, contact info, unit, transaction, invoice"
            sx={{ flex: '1 1 auto', minWidth: { sm: 180, md: 260 } }}
          />
          {filterControls}
        </Stack>
      )}
    </Stack>
  )
}
